// src/ui/screens/progress.ts
// PROGRESS — change over time, workload counts, and the batting block. THE SCREEN MOST LIKELY TO LIE.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THIS FILE IS SO CAREFUL
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Everything else in this app renders a fact the server looked up. This screen renders a JUDGEMENT
// the server computed, and a judgement has a failure mode a lookup does not: it can be rendered as
// a smaller, friendlier version of itself. "Not measurable" becomes a flat line. "We refused to
// compare these" becomes a zero. "The camera changed" becomes two dots with a line between them.
//
// The backend spent its whole design budget making that impossible on its side:
//
//   · src/practice/baselines.ts returns a `BaselineRefusal` that carries NO `verdict`, NO `delta`
//     and NO `central_value` key — not even null — precisely so nothing downstream can print a
//     refusal as zero. That shape is only useful if this file honours it, which is why the guards
//     below test the BRAND (`gate === 'PRACTICE_CHANGE_GATE_V1'`) and never `'verdict' in x`.
//   · `assessChange` has THREE outcomes plus a refusal, and the three are different sentences:
//       NOT_MEASURABLE                 — measured, compared, and the difference is inside this
//                                        athlete's own noise on this setup. THE HEADLINE CARRIES
//                                        THE CONSTANT `NO MEASURABLE CHANGE YET`.
//       MEASURABLE_BUT_NOT_WORTHWHILE  — big enough to see, smaller than what is worth acting on.
//       WORTHWHILE_CHANGE              — larger than both. `may_act` is true.
//     A REFUSAL IS NOT A FOURTH VERDICT. It is the absence of one, and it is rendered as its own
//     panel with the server's `code`, `reason`, `have` and `needed` — the real shortfall numbers.
//   · src/practice/workload-guardian.ts writes every athlete-facing sentence in one closed table
//     and attaches a fixed `scope_statement` to every response, in every state.
//   · src/practice/batting-service.ts throws — throws, not filters — on any emitted sentence that
//     reaches for a cause, a body part or an internal state. Its why-did-I-miss panel states counts
//     and BOTH denominators and carries `WHY_MISS_CAVEAT` with it.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE FOUR RULES THIS FILE KEEPS
// ══════════════════════════════════════════════════════════════════════════════════════════════
//   1. NOTHING IS FABRICATED. There is no demo mode, no placeholder series, no "sample data" and no
//      client-side arithmetic. Every number on screen was sent by the server. Today most of these
//      panels refuse for want of data, and the refusals are what the screen shows.
//   2. NO LINE IS EVER DRAWN ACROSS A REFUSAL. `changePlot()` is called from exactly one place and
//      only behind `isChangeVerdict()`. If the capture setup changed — a different phone, a
//      different frame-rate band — the server says the two points are not comparable, and joining
//      them would be the single most misleading thing this app could do.
//   3. NO MEDICAL OR PSYCHOLOGICAL LANGUAGE. Not in a label, not in an empty state, not in a
//      tooltip. The workload guardian deliberately BLOCKS NOTHING — it is not a medical boundary it
//      is allowed to draw — so this screen must not imply that a child is being held back from
//      anything. It says so out loud instead.
//   4. COUNTS, NOT PERCENTAGES. Nothing here is multiplied by 100, divided, or turned into a trend.
//      `beaten_share` is printed exactly as the server sent it, and only when the server sent one.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THE TYPES AND CALLS LIVE HERE INSTEAD OF IN api/practice.ts
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `api/practice.ts` is shared by six screens being built in parallel. These four routes are used by
// this screen and nothing else, so they are declared here rather than widening a shared file that
// five other lanes are editing. They still go through `request()` — the ONE place a request leaves
// this app — so the token handling, the 401 refresh and the refusal classification are unchanged.
// Transcribed from, not guessed at:
//   dcs-sports-backend/sb/src/practice/routes/baselines.ts + src/practice/baselines.ts
//   dcs-sports-backend/sb/src/practice/routes/workload.ts  + src/practice/workload-guardian.ts
//   dcs-sports-backend/sb/src/practice/routes/batting.ts   + src/practice/batting-service.ts
//
// WHY THE METRIC IS TYPED IN AND NOT PICKED FROM A LIST: no route in the practice namespace
// enumerates metric cards. There is no `GET /metrics`. Inventing a dropdown would mean shipping a
// hard-coded list of metric ids that drifts from the registry the moment a card is added, and a
// stale list on this screen would produce `unknown metric` refusals that look like missing data.
// The id is typed, the server validates it, and an unknown id comes back as a 400 the user can read.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal, request, type ApiResult } from '../../api/client';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import { getState } from '../state';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CHANGE GATE — transcribed from src/practice/baselines.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** The brand every object the gate returns carries. Checked, never assumed. */
export const CHANGE_GATE_BRAND = 'PRACTICE_CHANGE_GATE_V1';

/** The constant the backend pins in a test and the UI must not paraphrase. */
export const NO_MEASURABLE_CHANGE_YET = 'NO MEASURABLE CHANGE YET';

export const CHANGE_VERDICTS = [
  'NOT_MEASURABLE', 'MEASURABLE_BUT_NOT_WORTHWHILE', 'WORTHWHILE_CHANGE',
] as const;
export type ChangeVerdictCode = (typeof CHANGE_VERDICTS)[number];

/** One named shortfall, with the counts that make it actionable rather than merely discouraging. */
export interface Deficiency {
  code: string;
  measure: string;
  reason: string;
  have: number | string;
  needed: number | string;
}

/**
 * THE REFUSAL. Note what is NOT here: no `verdict`, no `delta`, no `central_value`, not even as
 * null. The backend shaped it this way on purpose and this type copies the omission, so a renderer
 * that reaches for a number on a refusal does not compile.
 */
export interface ChangeRefusalBody extends Deficiency {
  gate: string;
  computable: false;
  refused: true;
  deficiencies: readonly Deficiency[];
}

export interface ChangeVerdictBody {
  gate: string;
  computable: true;
  verdict: ChangeVerdictCode;
  /** Written for a human by the server. Rendered verbatim, always. */
  headline: string;
  metric_id: string;
  metric_label: string;
  unit: string | null;
  context_key: string;
  capture_setup_key: string;
  measurement_class: string;
  before: { value: number; n_sessions: number; n_observations: number; window_start: string; window_end: string };
  after: { value: number; n_sessions: number; n_observations: number; window_start: string; window_end: string };
  delta: number;
  abs_delta: number;
  direction: 'INCREASE' | 'DECREASE' | 'NONE';
  noise_floor: number;
  detection_threshold: number;
  smallest_worthwhile_change: number;
  baseline_state: string;
  /** The arithmetic spelled out, so the verdict can be recomputed from what is printed. */
  working: readonly string[];
  caveats: readonly string[];
}

/** A baseline that WAS computable. A refusal arrives in the same slot and is the other branch. */
export interface BaselineBody {
  gate: string;
  computable: true;
  metric_id: string;
  context_key: string;
  capture_setup_key: string;
  window_start: string;
  window_end: string;
  n_observations: number;
  n_sessions: number;
  /** null on a baseline too thin to carry one. Rendered as "not established", never as 0. */
  noise_floor: number | null;
  smallest_worthwhile_change: number | null;
  dispersion: number | null;
  state: string;
  unit: string | null;
  deficiencies: readonly Deficiency[];
  notes: readonly string[];
}

export interface ChangeReadResponse {
  athlete_id: string;
  viewer_role: string;
  metric_id: string;
  generated_at: string;
  comparison: 'EXPLICIT_SESSIONS' | 'LATEST_SESSION_VS_PRIOR_WINDOW';
  /** `BaselineBody | ChangeRefusalBody`. Kept `unknown` so it must pass a guard to be read. */
  baseline: unknown;
  /** `ChangeVerdictBody | ChangeRefusalBody`. Same reason. */
  assessment: unknown;
  may_act: boolean;
  refused: boolean;
}

function branded(x: unknown): Record<string, unknown> | null {
  const o = x as Record<string, unknown> | null | undefined;
  return o != null && typeof o === 'object' && o['gate'] === CHANGE_GATE_BRAND ? o : null;
}

/**
 * THE TWO GUARDS. Both check the brand, exactly as `isWorthwhile`/`isChangeRefusal` do on the
 * server, so an object assembled anywhere else — or a refusal spread into a wider object — cannot
 * impersonate a verdict. Neither guard tests for the PRESENCE of a `verdict` key, because "the key
 * is missing" is the very condition a careless client reads as permission.
 */
export function isChangeRefusal(x: unknown): x is ChangeRefusalBody {
  const o = branded(x);
  return o !== null && o['computable'] === false && o['refused'] === true;
}

export function isChangeVerdict(x: unknown): x is ChangeVerdictBody {
  const o = branded(x);
  return o !== null && o['computable'] === true
    && (CHANGE_VERDICTS as readonly string[]).includes(String(o['verdict']));
}

export function isBaseline(x: unknown): x is BaselineBody {
  const o = branded(x);
  return o !== null && o['computable'] === true && typeof o['state'] === 'string';
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * WORKLOAD — transcribed from src/practice/workload-guardian.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface NotComputable { measure: string; reason: string }

export interface WorkloadReport {
  athlete_id: string;
  viewer_role: string;
  generated_at: string;
  guidance_state: 'INSUFFICIENT_HISTORY' | 'WITHIN_OWN_BASELINE' | 'REVIEW_RECOMMENDED' | 'REST_SPACING_REVIEW';
  /** The guardian's own closed set of sentences. Rendered unedited, in order. */
  messages: string[];
  /** Present on every response, in every state. Rendered on every response, in every state. */
  scope_statement: string;
  current_window: {
    from: string; to: string;
    bowling_deliveries: number; sessions: number; days_with_a_session: number;
    max_deliveries_in_one_session: number; max_deliveries_in_one_day: number;
  };
  /** NULL when no baseline was established. Rendered as absent — never as a row of zeros. */
  baseline: {
    name: string; window_from: string; window_to: string; weeks_in_window: number;
    weeks_with_a_session: number; sessions_in_window: number; deliveries_in_window: number;
    mean_weekly_deliveries: number; ewma_weekly_deliveries: number; established: boolean;
  } | null;
  vs_baseline: { ratio: number; difference: number; direction: 'above' | 'below' | 'level' } | null;
  spells: {
    spells_in_window: number | null;
    max_deliveries_in_one_spell: number | null;
    deliveries_without_spell_number: number;
  };
  rest: {
    gaps: Array<{ from_session_id: string; to_session_id: string; hours: number; measured_from_session_start: boolean; below_academy_setting: boolean }>;
    shortest_gap_hours: number | null;
    academy_min_rest_hours: number;
    age_group_used: string;
  };
  /** WHETHER a session captured a context value — never the value itself. It lives in the vault. */
  intensity_context: {
    sessions_with_context_in_vault: number;
    sessions_context_declined: number;
    sessions_context_not_captured: number;
    note: string;
  };
  not_computable: NotComputable[];
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * BATTING — transcribed from src/practice/batting-service.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface BlockResolution {
  requested: number; preset: number | null; size: number; custom: boolean; intent: string;
  /** Never padded to `size`. The gap between these two is the honest part. */
  balls_available: number; balls_analysed: number;
}

/** Where the ball-type label came from. Rendered as the fact that keeps the four modes distinct. */
export interface InputProvenance {
  tier: string;
  provenance: string;
  requires_metric_card: boolean;
  why: string;
}

export interface BallTypeCell {
  ball_type: string; label: string;
  balls: number; played: number; edged: number; beaten: number; left: number; unresolved: number;
  /** null below the cell minimum — an absent key, never a zero. Printed as sent, never as a %. */
  beaten_share: number | null;
  sample_note: string | null;
}

export interface BallTypeMapResponse {
  session_id: string;
  mode: string;
  surface: string;
  block: BlockResolution;
  input: InputProvenance;
  map: {
    state: 'PRODUCED' | 'REFUSED';
    refusal_code: 'WINDOW_TOO_SMALL' | 'BALL_TYPE_NOT_RECORDED' | 'METRIC_NOT_PERMITTED' | null;
    message: string;
    window_balls: number;
    window_label: string;
    cells: readonly BallTypeCell[];
  };
  scope_note: string;
}

/** The eight. Each has its own wording on the server; a locked panel is a correct outcome. */
export const WHY_MISS_LOCK_REASONS = [
  'TOO_FEW_BALLS_IN_BLOCK',
  'BALL_TYPE_NOT_RECORDED',
  'TOO_FEW_MISSES_OF_TYPE',
  'NO_COMPARISON_DENOMINATOR',
  'NO_QUALIFYING_OBSERVATION',
  'NO_DIFFERENTIAL',
  'METRIC_NOT_PERMITTED_ON_SURFACE',
  'COACH_DISMISSED',
] as const;
export type WhyMissLockReason = (typeof WHY_MISS_LOCK_REASONS)[number];

export interface WhyMissResponse {
  session_id: string;
  mode: string;
  surface: string;
  block: BlockResolution;
  input: InputProvenance;
  counts: Record<string, number>;
  panel: {
    state: 'UNLOCKED' | 'LOCKED';
    lock_reason: WhyMissLockReason | null;
    message: string;
    /** null unless UNLOCKED. Counts and BOTH denominators. Never a cause. */
    observation: string | null;
    /** Travels with the observation, every time. See `attachCaveat` below. */
    caveat: string;
    ball_type: string | null;
    ball_type_label: string | null;
    counts: { missed: number; missed_with_observable: number; played: number; played_with_observable: number } | null;
    clips: { missed: readonly unknown[]; played: readonly unknown[] };
    evidence: {
      measurement_class: string | null;
      min_confidence_required: number;
      observations_used: number;
      observations_excluded: ReadonlyArray<{ metric_id: string; reason: string; detail: string }>;
    };
    coach: { disposition: string; comment: string | null; review_path: string };
    drill: unknown;
    drill_note: string;
  };
  scope_note: string;
  not_detectable: readonly NotComputable[];
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE FOUR CALLS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** GET …/baselines/:metricId/change — the gate. Three outcomes, or a refusal. */
export function changeGate(athleteId: string, metricId: string): Promise<ApiResult<ChangeReadResponse>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/baselines/${encodeURIComponent(metricId)}/change`,
  );
}

/** GET …/workload — counts, the guardian's sentences, and its scope statement. */
export function workloadReport(athleteId: string): Promise<ApiResult<WorkloadReport>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/workload`);
}

/** GET …/batting/ball-type-map — REFUSED arrives as a 200 carrying its machine-readable reason. */
export function ballTypeMap(sessionId: string): Promise<ApiResult<BallTypeMapResponse>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/batting/ball-type-map`);
}

/** GET …/batting/why-did-i-miss — LOCKED is a 200, and a finding. See the router's header. */
export function whyDidIMiss(sessionId: string): Promise<ApiResult<WhyMissResponse>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/batting/why-did-i-miss`);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE METRIC UNDER LOOK. Module-level so a re-render keeps it; see the header for why it is typed.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

let metricId: string | null = null;

export function setProgressMetric(id: string | null): void {
  metricId = id !== null && id.trim() !== '' ? id.trim() : null;
}
export function getProgressMetric(): string | null {
  return metricId;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SMALL RENDER HELPERS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A number that may legitimately be absent. NEVER falls back to 0.
 *
 * "The noise floor is not established" and "the noise floor is zero" are opposite claims, and a
 * `?? 0` anywhere on this screen turns the first into the second.
 */
function orAbsent(v: number | string | null | undefined, absent: string): string | number {
  return v === null || v === undefined || v === '' ? absent : v;
}

function lines(cls: string, items: readonly string[]): HTMLElement | false {
  return items.length > 0
    && el('div', { class: 'list' }, ...items.map((s) => el('p', { class: cls }, s)));
}

function deficiencyRows(items: readonly Deficiency[]): HTMLElement | false {
  return items.length > 0 && el('div', { class: 'list' }, ...items.map((d) => el('div', { class: 'row-item' },
    field('Missing', d.code),
    field('Measure', d.measure),
    el('p', { class: 'note' }, d.reason),
    // THE SHORTFALL, in the server's own counts. This is the honest number on a screen that has
    // nothing else to show, and it is why a refusal is not a dead end for the person reading it.
    field('Have', d.have),
    field('Needed', d.needed),
  )));
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE PLOT — TWO POINTS, AND THE ONLY LINE THIS APP EVER DRAWS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

const SVG_NS = 'http://www.w3.org/2000/svg';

function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
  ...children: Array<Node | string>
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  for (const c of children) node.append(typeof c === 'object' ? c : document.createTextNode(String(c)));
  return node;
}

/**
 * BEFORE AND AFTER, JOINED — and the band the server says a difference must clear to be seen at all.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONLY CALLER OF THIS FUNCTION IS BEHIND `isChangeVerdict()`. THAT IS THE WHOLE POINT.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * A verdict exists only after the server has checked — BEFORE any arithmetic — that the two sides
 * share a metric, a card version, a context, a capture setup, a measurement class and a unit. The
 * line below is therefore a line between two points the SERVER has certified as comparable. When it
 * has not, there is no verdict, this function is never reached, and the panel shows the refusal.
 *
 * The shaded band is ±`detection_threshold` around the BEFORE value. It is drawn so that
 * NOT_MEASURABLE looks like what it is — a second point sitting inside the band — instead of
 * looking like a flat line or an empty chart. Every number here came from the response; nothing is
 * interpolated, smoothed, extrapolated or projected, and there are exactly two points because the
 * server sent exactly two.
 */
export function changePlot(v: ChangeVerdictBody): SVGSVGElement | null {
  const b = v.before.value;
  const a = v.after.value;
  const t = v.detection_threshold;
  if (![b, a, t].every((n) => typeof n === 'number' && Number.isFinite(n))) return null;

  const pad = Math.max(Math.abs(a - b), Math.abs(t), 1e-9) * 1.4;
  const lo = Math.min(b, a) - pad;
  const hi = Math.max(b, a) + pad;
  const span = hi - lo || 1;
  const y = (val: number): number => 104 - ((val - lo) / span) * 84;
  const unit = v.unit ? ` ${v.unit}` : '';

  const root = svg('svg', {
    class: 'change-plot', viewBox: '0 0 320 128', width: '100%', height: '128',
    role: 'img', 'aria-label': `${v.metric_label}: ${v.before.value}${unit} then ${v.after.value}${unit}`,
  });
  root.append(
    // The band a difference must clear before this setup can tell it apart from the tracker.
    svg('rect', {
      x: 8, y: Math.min(y(b + t), y(b - t)), width: 304,
      height: Math.max(1, Math.abs(y(b - t) - y(b + t))),
      fill: 'currentColor', opacity: '0.10',
    }),
    // ── THE LINE. Drawn only here, only behind a verdict.
    svg('line', {
      class: 'change-link', x1: 60, y1: y(b), x2: 260, y2: y(a),
      stroke: 'currentColor', 'stroke-width': '2',
    }),
    svg('circle', { class: 'change-point', cx: 60, cy: y(b), r: 5, fill: 'currentColor' }),
    svg('circle', { class: 'change-point', cx: 260, cy: y(a), r: 5, fill: 'currentColor' }),
    svg('text', { x: 60, y: 122, 'text-anchor': 'middle', 'font-size': '11', fill: 'currentColor' },
      `before ${v.before.value}${unit}`),
    svg('text', { x: 260, y: 122, 'text-anchor': 'middle', 'font-size': '11', fill: 'currentColor' },
      `after ${v.after.value}${unit}`),
  );
  return root;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SECTION 1 — CHANGE OVER TIME
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A REFUSAL, RENDERED AS A REFUSAL.
 *
 * No verdict, no delta, no plot, no zero — and, deliberately, no reassurance either. The server's
 * `reason` is a paragraph written for a human; it is printed unedited, with the shortfall counts
 * beside it and every other named deficiency underneath.
 */
function changeRefusalPanel(r: ChangeRefusalBody): HTMLElement {
  return el('div', { class: 'gate-refusal row-item' },
    el('p', { class: 'progress-what' }, 'This could not be measured'),
    field('Refusal', r.code),
    field('Measure', r.measure),
    el('p', { class: 'progress-sentence' }, r.reason),
    field('Have', r.have),
    field('Needed', r.needed),
    el('p', { class: 'note' },
      'Nothing was compared, so there is no result to show and none is being shown. An absent '
      + 'comparison is not a measurement that came back flat.'),
    r.deficiencies.length > 1
      && el('details', {}, el('summary', {}, 'Everything else that is missing'),
        deficiencyRows(r.deficiencies.slice(1))),
  );
}

/** A verdict, rendered with the server's own headline, its working, and the two points it joined. */
function changeVerdictPanel(v: ChangeVerdictBody, mayAct: boolean): HTMLElement {
  const plot = changePlot(v);
  return el('div', { class: 'gate-verdict row-item' },
    el('p', { class: 'progress-what' }, v.metric_label),
    field('Verdict', v.verdict),
    // ── THE SERVER'S SENTENCE. For NOT_MEASURABLE this carries `NO MEASURABLE CHANGE YET` at its
    //    head, followed by the server's own explanation of what it would take to see a difference.
    el('p', { class: 'progress-sentence' }, v.headline),
    plot,
    el('div', { class: 'counts' },
      field('Before', v.before.value),
      field('After', v.after.value),
      field('Change', v.delta),
      field('Direction', v.direction),
      field('Noise floor', v.noise_floor),
      field('Detection threshold', v.detection_threshold),
      field('Smallest worthwhile change', v.smallest_worthwhile_change),
      field('Unit', orAbsent(v.unit, 'none recorded')),
    ),
    // Only under a verdict. Beside a refusal, "may act: no" would read as a measured "no", which is
    // exactly the collapse this screen exists to prevent.
    field('Worth acting on', mayAct ? 'yes' : 'no'),
    field('Baseline state', v.baseline_state),
    field('Capture setup', v.capture_setup_key),
    field('Context', v.context_key),
    el('details', {}, el('summary', {}, 'The arithmetic, in full'), lines('mono', v.working)),
    lines('note', v.caveats),
  );
}

function baselinePanel(x: unknown): HTMLElement {
  if (isChangeRefusal(x)) return changeRefusalPanel(x);
  if (!isBaseline(x)) {
    return empty('The server did not return a baseline object this app recognises.');
  }
  return el('div', { class: 'row-item' },
    field('Baseline state', x.state),
    field('Sessions', x.n_sessions),
    field('Observations', x.n_observations),
    field('Noise floor', orAbsent(x.noise_floor, 'not established')),
    field('Smallest worthwhile change', orAbsent(x.smallest_worthwhile_change, 'not established')),
    field('Window', `${x.window_start} → ${x.window_end}`),
    field('Capture setup', x.capture_setup_key),
    lines('note', x.notes),
    deficiencyRows(x.deficiencies),
  );
}

async function changeSection(athleteId: string, rerender: () => void): Promise<HTMLElement> {
  const chosen = metricId;
  const input = el('input', {
    class: 'input', type: 'text', id: 'progress-metric',
    value: chosen ?? '', placeholder: 'metric id, e.g. bat_head_movement',
    'aria-label': 'Metric id',
  });
  const form = el('div', { class: 'row' },
    input,
    el('button', {
      class: 'btn btn-primary',
      onclick: () => { setProgressMetric(input.value); rerender(); },
    }, 'Look at change'),
  );
  const why = el('p', { class: 'note' },
    'The metric id is typed in: no route on this server lists the metric cards, and a hard-coded '
    + 'list here would drift from the registry. An id this deployment does not know comes back as a '
    + 'rejection you can read, rather than as missing data.');

  if (!chosen) {
    return section('Change over time', form, why,
      empty('No metric chosen yet. Nothing is being shown, and nothing is being assumed.'));
  }

  const r = await changeGate(athleteId, chosen);
  if (isRefusal(r)) {
    return section('Change over time', form, why, refusalView(r, { onRetry: rerender }));
  }
  const v = r.value;

  const assessment = isChangeRefusal(v.assessment)
    ? changeRefusalPanel(v.assessment)
    : isChangeVerdict(v.assessment)
      ? changeVerdictPanel(v.assessment, v.may_act === true)
      // Neither branded shape. Shown as the unrecognised thing it is, never as an empty result.
      : el('div', { class: 'row-item' },
        el('p', { class: 'progress-sentence' },
          'The server answered in a shape this app does not recognise, so nothing is being claimed '
          + 'from it.'),
        el('pre', { class: 'mono' }, JSON.stringify(v.assessment, null, 2)));

  return section('Change over time',
    form, why,
    field('Metric', v.metric_id),
    field('Comparison', v.comparison),
    field('Your role', v.viewer_role),
    assessment,
    el('h3', {}, 'The baseline it was judged against'),
    baselinePanel(v.baseline),
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SECTION 2 — WORKLOAD
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THIS SURFACE STOPS NOTHING, AND THE SCREEN SAYS SO.
 *
 * The guardian counts balls bowled in practice and compares them against this athlete's OWN recent
 * weeks. It has no fifth state meaning "safe", it refuses to emit a ratio built from ball counts as
 * a decision threshold, and it deliberately does not block or cap anything — that boundary is not
 * one this product is allowed to draw. A UI that rendered it as a limit, a gauge or a green tick
 * would be claiming a judgement the backend went out of its way not to make.
 */
function workloadSection(w: WorkloadReport): HTMLElement {
  const c = w.current_window;
  return section('Workload',
    // The fixed sentence, present on every response in every state — so it is rendered on every
    // response in every state, above everything else.
    el('p', { class: 'note' }, w.scope_statement),
    el('p', { class: 'note' },
      'Nothing on this screen stops, limits or approves a session. It reports counts and the '
      + "guardian's own sentences, and the decision stays with the coach."),
    field('State', w.guidance_state),
    // The guardian's own sentences, unedited and in order.
    lines('progress-sentence', w.messages),

    el('h3', {}, 'This window, counted'),
    field('Window', `${c.from} → ${c.to}`),
    el('div', { class: 'counts' },
      field('Balls bowled', c.bowling_deliveries),
      field('Sessions', c.sessions),
      field('Days with a session', c.days_with_a_session),
      field('Most in one session', c.max_deliveries_in_one_session),
      field('Most in one day', c.max_deliveries_in_one_day),
    ),

    el('h3', {}, "This athlete's own baseline"),
    w.baseline
      ? el('div', { class: 'row-item' },
        field('Compared against', w.baseline.name),
        field('Established', w.baseline.established ? 'yes' : 'no'),
        field('Weeks in window', w.baseline.weeks_in_window),
        field('Weeks with a session', w.baseline.weeks_with_a_session),
        field('Sessions in window', w.baseline.sessions_in_window),
        field('Balls in window', w.baseline.deliveries_in_window),
        field('Mean balls per week', w.baseline.mean_weekly_deliveries),
        field('Weighted mean balls per week', w.baseline.ewma_weekly_deliveries),
      )
      // NOT a row of zeros. No baseline was established, and the guardian's message above says why.
      : empty('No baseline was established, so nothing is being compared. The sentences above say '
        + 'what was missing.'),
    w.vs_baseline && el('div', { class: 'row-item' },
      field('Against own weekly mean', w.vs_baseline.direction),
      field('Difference in balls', w.vs_baseline.difference),
      field('Ratio to own weekly mean', w.vs_baseline.ratio),
    ),

    el('h3', {}, 'Spells'),
    el('div', { class: 'counts' },
      field('Spells in window', orAbsent(w.spells.spells_in_window, 'not recorded')),
      field('Most in one spell', orAbsent(w.spells.max_deliveries_in_one_spell, 'not recorded')),
      field('Balls with no spell number', w.spells.deliveries_without_spell_number),
    ),

    el('h3', {}, 'Time between bowling sessions'),
    field('Academy setting (hours)', w.rest.academy_min_rest_hours),
    field('Age group used', w.rest.age_group_used),
    field('Shortest gap (hours)', orAbsent(w.rest.shortest_gap_hours, 'no two sessions to measure between')),
    w.rest.gaps.length
      ? el('div', { class: 'list' }, ...w.rest.gaps.map((g) => el('div', { class: 'row-item' },
        field('Gap (hours)', g.hours),
        field('Below the academy setting', g.below_academy_setting ? 'yes' : 'no'),
        g.measured_from_session_start
          && el('p', { class: 'note' },
            'The earlier session has no end time recorded, so this gap was measured from when it started.'),
      )))
      : empty('No gap between two bowling sessions was measurable in this window.'),

    el('h3', {}, 'Session context'),
    el('p', { class: 'note' }, w.intensity_context.note),
    el('div', { class: 'counts' },
      field('Captured, held in the vault', w.intensity_context.sessions_with_context_in_vault),
      field('Declined', w.intensity_context.sessions_context_declined),
      field('Not captured', w.intensity_context.sessions_context_not_captured),
    ),

    el('h3', {}, 'What this screen does not report'),
    el('div', { class: 'list' }, ...w.not_computable.map((n) => el('div', { class: 'row-item' },
      field('Not reported', n.measure),
      el('p', { class: 'note' }, n.reason),
    ))),
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SECTION 3 — BATTING
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

function blockFields(b: BlockResolution): HTMLElement {
  return el('div', { class: 'counts' },
    field('Block asked for', b.requested),
    field('Intent', b.intent),
    // Never padded to `size`. When fewer balls exist than were asked for, that is what is shown.
    field('Balls available', b.balls_available),
    field('Balls analysed', b.balls_analysed),
    field('Custom size', b.custom ? 'yes' : 'no'),
  );
}

function inputFields(i: InputProvenance): HTMLElement {
  return el('div', { class: 'row-item' },
    field('Ball-type input', i.tier),
    field('Where it came from', i.provenance),
    field('Held to a metric card', i.requires_metric_card ? 'yes' : 'no'),
    el('p', { class: 'note' }, i.why),
  );
}

function ballTypeMapPanel(r: BallTypeMapResponse): HTMLElement {
  const m = r.map;
  if (m.state === 'REFUSED') {
    return el('div', { class: 'gate-refusal row-item' },
      el('p', { class: 'progress-what' }, 'No ball-type breakdown'),
      field('Refusal', orAbsent(m.refusal_code, 'not named')),
      // The server's own sentence. No cells, no zero rows, no placeholder table.
      el('p', { class: 'progress-sentence' }, m.message),
      field('Balls in the window', m.window_balls),
    );
  }
  return el('div', {},
    el('p', { class: 'note' }, m.message),
    field('Window', m.window_label),
    field('Balls in the window', m.window_balls),
    m.cells.length
      ? el('div', { class: 'list' }, ...m.cells.map((c) => el('div', { class: 'row-item ball-type-cell' },
        el('p', { class: 'progress-what' }, c.label),
        el('div', { class: 'counts' },
          field('Balls', c.balls),
          field('Played', c.played),
          field('Edged', c.edged),
          field('Beaten', c.beaten),
          field('Left', c.left),
          field('Unresolved', c.unresolved),
        ),
        // Printed exactly as the server sent it. Not multiplied by 100, not rounded, not inferred —
        // and absent, with the server's note in its place, whenever the cell was too small.
        c.beaten_share === null
          ? el('p', { class: 'note' },
            orAbsent(c.sample_note, 'Too few balls of this type for a share to be shown.'))
          : field('Beaten share of resolved shot-attempted balls', c.beaten_share),
      )))
      : empty('No ball-type cells were produced for this window.'),
  );
}

/**
 * THE WHY-DID-I-MISS PANEL.
 *
 * UNLOCKED: the server's sentence — counts and BOTH denominators — and the caveat that limits it.
 * The two are appended to the SAME element, in one expression, so there is no code path on which a
 * future edit renders the claim and drops the limit. That pairing is the panel's whole safety
 * property: the sentence is a co-occurrence count, and without the caveat a reader will hear a
 * cause.
 *
 * LOCKED: the lock reason and the server's wording for it. Eight reasons, eight different
 * sentences, and a locked panel is a CORRECT OUTCOME — a 200, not a 404, not a fault, not something
 * to retry around.
 */
function whyMissPanel(r: WhyMissResponse): HTMLElement {
  const p = r.panel;
  if (p.state === 'LOCKED') {
    return el('div', { class: 'why-miss-panel why-miss-locked row-item' },
      el('p', { class: 'progress-what' }, 'Nothing is being shown here'),
      field('Reason', orAbsent(p.lock_reason, 'not named')),
      el('p', { class: 'progress-sentence' }, p.message),
      el('p', { class: 'note' },
        'A closed panel is a correct outcome. It means the evidence bar was not met, not that '
        + 'something went wrong.'),
    );
  }
  const c = p.counts;
  return el('div', { class: 'why-miss-panel why-miss-unlocked row-item' },
    el('p', { class: 'progress-what' }, orAbsent(p.ball_type_label, 'This block')),
    el('p', { class: 'why-miss-observation progress-sentence' }, orAbsent(p.observation, p.message)),
    // ── THE CAVEAT TRAVELS WITH THE OBSERVATION. Same element, same expression, always.
    el('p', { class: 'why-miss-caveat note' }, p.caveat),
    c && el('div', { class: 'counts' },
      field('Missed', c.missed),
      field('Missed with the movement measured', c.missed_with_observable),
      field('Successfully played', c.played),
      field('Played with the movement measured', c.played_with_observable),
    ),
    el('div', { class: 'counts' },
      field('Clips behind the missed count', p.clips.missed.length),
      field('Clips behind the played count', p.clips.played.length),
    ),
    field('Measured as', orAbsent(p.evidence.measurement_class, 'not recorded')),
    field('Observations used', p.evidence.observations_used),
    field('Coach decision', p.coach.disposition),
    p.coach.comment && el('p', { class: 'note' }, p.coach.comment),
    field('Where a coach records it', p.coach.review_path),
    p.evidence.observations_excluded.length > 0
      && el('details', {}, el('summary', {}, 'Measurements left out, and why they did not qualify'),
        el('div', { class: 'list' }, ...p.evidence.observations_excluded.map((x) => el('div', { class: 'row-item' },
          field('Metric', x.metric_id),
          field('Left out', x.reason),
          el('p', { class: 'note' }, x.detail),
        )))),
    el('p', { class: 'note' }, p.drill_note),
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export async function progressScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  if (!st.athlete) {
    mount(root, section('Progress',
      empty('No athlete selected. Pick one on the Coach screen or the Parent screen — this app only '
        + 'ever uses an athlete id the server handed it.')));
    return;
  }
  const athleteId = st.athlete.athleteId;
  const sessionId = st.sessionId;
  mount(root, spinner('Loading progress, workload and batting…'));

  // Independent reads, in parallel. Each renders its own refusal; one refusing never blanks another.
  const [change, work, map, why] = await Promise.all([
    changeSection(athleteId, rerender),
    workloadReport(athleteId),
    sessionId ? ballTypeMap(sessionId) : Promise.resolve(null),
    sessionId ? whyDidIMiss(sessionId) : Promise.resolve(null),
  ]);

  const out = el('div');
  out.append(change);

  out.append(isRefusal(work)
    ? section('Workload', refusalView(work, { onRetry: rerender }))
    : workloadSection(work.value));

  if (!sessionId) {
    out.append(section('Batting block',
      empty('No practice session selected. The batting block describes one session, so there is '
        + 'nothing to describe until one is chosen.')));
  } else {
    out.append(section('Batting — response by ball type',
      map === null ? empty('Not requested.')
        : isRefusal(map) ? refusalView(map, { onRetry: rerender })
          : el('div', {},
            el('p', { class: 'note' }, map.value.scope_note),
            blockFields(map.value.block),
            inputFields(map.value.input),
            ballTypeMapPanel(map.value)),
    ));

    out.append(section('Batting — why did I miss',
      why === null ? empty('Not requested.')
        : isRefusal(why) ? refusalView(why, { onRetry: rerender })
          : el('div', {},
            el('p', { class: 'note' }, why.value.scope_note),
            blockFields(why.value.block),
            el('div', { class: 'counts' },
              ...Object.entries(why.value.counts).map(([k, n]) => field(k, n))),
            whyMissPanel(why.value),
            el('details', {}, el('summary', {}, 'What no metric card here can detect'),
              el('div', { class: 'list' }, ...why.value.not_detectable.map((n) => el('div', { class: 'row-item' },
                field('Not detectable', n.measure),
                el('p', { class: 'note' }, n.reason),
              ))))),
    ));
  }

  mount(root, out);
}
